import { useEffect, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import ClientLocationsEditor from './client-crm/ClientLocationsEditor'
import ClientServicesEditor from './client-crm/ClientServicesEditor'
import ClientPartnersEditor from './client-crm/ClientPartnersEditor'
import ClientEquipmentEditor from './client-crm/ClientEquipmentEditor'
import ClientEmployeesEditor from './client-crm/ClientEmployeesEditor'
import * as FiIcons from 'react-icons/fi'

const sections = [
  ['locations', 'Locations', FiIcons.FiMapPin],
  ['services', 'Services', FiIcons.FiLayers],
  ['partners', 'Partners', FiIcons.FiUserCheck],
  ['equipment', 'Equipment', FiIcons.FiCpu],
  ['employees', 'Employees', FiIcons.FiUsers]
]

export default function ClientOperationsEditor({ client, onChanged }) {
  const [section, setSection] = useState('locations')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [lookups, setLookups] = useState({ services: [], partners: [], brands: [], models: [] })

  useEffect(() => { loadLookups() }, [])

  async function loadLookups() {
    try {
      setLoading(true)
      const [services, partners, brands, models] = await Promise.all([
        supabase.from('codevaro_services').select('id,title,is_active,active').order('title'),
        supabase.from('codevaro_partners').select('*').order('name'),
        supabase.from('codevaro_asset_brands').select('*').order('name'),
        supabase.from('codevaro_asset_models').select('*').order('model_name')
      ])
      const failed = [services, partners, brands, models].find((result) => result.error)
      if (failed) throw failed.error
      setLookups({ services: services.data || [], partners: partners.data || [], brands: brands.data || [], models: models.data || [] })
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function changed() {
    setRefreshKey((value) => value + 1)
    if (onChanged) await onChanged()
  }

  if (loading) return <div className="flex h-56 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" /></div>

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <strong>Operational editor:</strong> these forms write directly to the existing admin-protected CRM tables. Imported source rows remain preserved.
      </div>
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {sections.map(([key, label, icon]) => (
          <button key={key} onClick={() => setSection(key)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm ${section === key ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 shadow-sm hover:bg-slate-100'}`}>
            <SafeIcon icon={icon} className="h-4 w-4" />{label}
          </button>
        ))}
      </div>
      {section === 'locations' && <ClientLocationsEditor key={`locations-${refreshKey}`} client={client} onChanged={changed} />}
      {section === 'services' && <ClientServicesEditor key={`services-${refreshKey}`} client={client} services={lookups.services} onChanged={changed} />}
      {section === 'partners' && <ClientPartnersEditor key={`partners-${refreshKey}`} client={client} partners={lookups.partners} onChanged={changed} />}
      {section === 'equipment' && <ClientEquipmentEditor key={`equipment-${refreshKey}`} client={client} services={lookups.services} brands={lookups.brands} models={lookups.models} onChanged={changed} />}
      {section === 'employees' && <ClientEmployeesEditor key={`employees-${refreshKey}`} client={client} services={lookups.services} onChanged={changed} />}
    </div>
  )
}